import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { CreditBadge } from '../components/CreditBadge';
import { DocManiaLogoImage } from '../components/DocManiaLogo';
import { ProposalStepIndicator } from '../components/proposals/ProposalStepIndicator';
import {
  RERUN_TEMPLATE_FALLBACK_MESSAGE,
  applyRerunTemplateToSearchParams,
  resolveRerunTemplate,
} from '../lib/rerunTemplateFallback';
import {
  scrollToResultCard,
  formatResultCardMeta,
} from '../lib/documentGenerationUi';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, Loader2, CheckCircle, Download, Plus, Trash2,
  Briefcase, Building2, Target, Package, Coins, Users, Sparkles, FileText,
  Upload, X, ImageIcon, RefreshCw, Landmark, ShieldAlert,
} from 'lucide-react';
import './BusinessPlanWizardPage.css';
import { IndustrySelector } from '../components/ui/IndustrySelector';
import {
  INDUSTRY_OTHER,
  normalizeIndustryFields,
  resolveIndustry,
} from '../utils/industries';

const GRANT_PROPOSAL_CATEGORY = 'business_grant_proposal';
import { CurrencySelector } from '../components/ui/CurrencySelector';

/* ─── Constants ────────────────────────────────────────────────────────── */
// Proposal type is fixed at this layer — the user already picked the
// category on /documents/business-proposal/categories before reaching here.
const PROPOSAL_TYPE = 'Business Grant Proposal';

const STEPS = [
  { id: 1, title: 'Identity',     icon: Building2, hint: 'Your business basics' },
  { id: 2, title: 'Context',      icon: Target,    hint: 'What & why you do it' },
  { id: 3, title: 'Grant Plan',   icon: Landmark,  hint: 'Grant utilization plan' },
  { id: 4, title: 'Products',     icon: Package,   hint: 'What you sell' },
  { id: 5, title: 'Expenses',     icon: Coins,     hint: 'Monthly running cost' },
  { id: 6, title: 'Team',         icon: Users,     hint: 'Who runs it' },
  { id: 7, title: 'Risk & Mitigation', icon: ShieldAlert, hint: 'Key risks' },
  { id: 8, title: 'Suggestions', icon: Sparkles,  hint: 'Review & regenerate' },
  { id: 9, title: 'Review',       icon: FileText,  hint: 'Generate proposal' },
];

const WIZARD_STEP_COUNT = STEPS.length;

const AI_NARRATIVE_SECTIONS = [
  { key: 'executive_summary',    label: 'Executive Summary' },
  { key: 'business_description', label: 'Business Description' },
  { key: 'market_analysis',      label: 'Market Analysis' },
  { key: 'competitive_advantage',label: 'Competitive Advantage' },
  { key: 'operations_plan',      label: 'Operations Plan' },
  { key: 'closing_summary',      label: 'Closing Summary' },
];

const NARRATIVE_SECTION_KEY_SET = new Set(AI_NARRATIVE_SECTIONS.map(({ key }) => key));

/** Drop legacy / non-narrative keys (e.g. risk_and_mitigation from older drafts). */
function sanitizeAiNarratives(narratives = {}) {
  return Object.fromEntries(
    Object.entries(narratives).filter(([key]) => NARRATIVE_SECTION_KEY_SET.has(key)),
  );
}

/** Read-only Step 8 preview — sourced from Step 7 rows, not AI narrative. */
const RISK_PREVIEW_SECTION = { key: 'risk_preview', label: 'Risk & Mitigation' };

const BLANK_PRODUCT  = { name: '', description: '', cost_price: '', selling_price: '', monthly_qty: '' };
const BLANK_EXPENSE  = { name: '', monthly_cost: '' };
const BLANK_TEAMER   = { name: '', role: '', responsibility: '' };
const BLANK_GRANT    = { item: '', purpose: '', amount: '' };
const RISK_LEVELS    = ['Low', 'Medium', 'High'];
const BLANK_RISK     = { risk_name: '', risk_level: 'Medium', risk_mitigation: '' };

const normalizeGrantRisk = (r = {}) => ({
  risk_name: (r.risk_name || r.risk_factor || r.name || '').trim(),
  risk_level: r.risk_level || r.risk_likelihood || 'Medium',
  risk_mitigation: r.risk_mitigation || r.mitigation || '',
});
const emptyGrantRisk = () => ({ ...BLANK_RISK });

/** Rows with both Risk Factor and Mitigation Strategy — required before leaving Step 7. */
function completedRiskRows(risks) {
  return (Array.isArray(risks) ? risks : [])
    .map(normalizeGrantRisk)
    .filter((r) => r.risk_name && r.risk_mitigation);
}

const defaultGrantRows = () => Array.from({ length: 4 }, () => ({ ...BLANK_GRANT }));

/** Ensure grant table always has at least 4 editable rows after draft/rerun restore. */
function normalizeGrantItems(items) {
  const rows = (Array.isArray(items) ? items : []).map((g) => ({
    item: g?.item ?? g?.category ?? '',
    purpose: g?.purpose ?? g?.description ?? '',
    amount: g?.amount != null && g?.amount !== '' ? String(g.amount) : '',
  }));
  while (rows.length < 4) rows.push({ ...BLANK_GRANT });
  return rows;
}

const INITIAL_DATA = {
  // Identity
  business_name: '', business_address: '', business_email: '', business_phone: '',
  business_website: '', grant_body: '',
  proposal_type: PROPOSAL_TYPE,
  currency: 'NGN',
  // Context
  industry: '',
  industry_sector: '',
  industry_other: '',
  nature_of_business: '', business_stage: '',
  business_goals: '', funding_purpose: '', target_market: '',
  problem_solved: '', competitive_positioning: '',
  funding_amount: '',
  // Products / expenses / team
  products: [{ ...BLANK_PRODUCT }],
  operating_expenses: [{ ...BLANK_EXPENSE }],
  management_team: [{ ...BLANK_TEAMER }],
  // SWOT & grant utilisation (4 editable rows by default)
  swot: { strengths: [''], weaknesses: [''], opportunities: [''], threats: [''] },
  grant_items: defaultGrantRows(),
  risks: [emptyGrantRisk()],
};

/* ─── Local helpers ────────────────────────────────────────────────────── */
const stripCommas = (v) => String(v ?? '').replaceAll(',', '').trim();

function RepeatableRow({ children, onRemove, removable }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-border bg-card/50 p-3">
      <div className="grid flex-1 gap-2">{children}</div>
      {removable && (
        <Button type="button" variant="ghost" size="icon" onClick={onRemove} className="text-muted-foreground hover:text-destructive">
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

function FieldRow({ label, hint, children, required }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium">
        {label} {required && <span className="text-destructive">*</span>}
      </Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

/* ─── Image upload widget ──────────────────────────────────────────────── */
const LARGE_PROJECT_IMAGE_BYTES = 2 * 1024 * 1024;
const LARGE_PROJECT_IMAGE_WARNING =
  'This image is large and will be automatically resized to fit the cover page.';

function ImagePicker({ label, file, setFile, hint, testId, warnIfLargerThan }) {
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreview(null);
  }, [file]);

  const handle = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error('Image too large — keep under 5 MB.');
      return;
    }
    if (!f.type.startsWith('image/')) {
      toast.error('Please pick an image file.');
      return;
    }
    setFile(f);
  };

  return (
    <FieldRow label={label} hint={hint}>
      {preview ? (
        <div className="flex items-start gap-3">
          <div className="relative inline-block">
            <img src={preview} alt={label} className="h-32 w-32 rounded-lg border object-cover" />
            <button type="button" onClick={() => setFile(null)} className="absolute -top-2 -right-2 rounded-full bg-destructive p-1 text-white shadow" data-testid={`${testId}-remove`}>
              <X className="h-3 w-3" />
            </button>
          </div>
          <label className="flex h-32 flex-1 cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-border bg-muted/30 px-3 text-center hover:bg-muted">
            <ImageIcon className="h-5 w-5 text-muted-foreground" />
            <span className="mt-1 text-xs text-muted-foreground">Replace</span>
            <input type="file" accept="image/*" onChange={handle} className="hidden" data-testid={testId} />
          </label>
        </div>
      ) : (
        <label className="flex h-32 w-32 cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-border bg-muted/40 hover:bg-muted">
          <ImageIcon className="h-6 w-6 text-muted-foreground" />
          <span className="mt-1 text-xs text-muted-foreground">Upload</span>
          <input type="file" accept="image/*" onChange={handle} className="hidden" data-testid={testId} />
        </label>
      )}
      {file && warnIfLargerThan && file.size > warnIfLargerThan && (
        <p className="mt-1 text-xs text-muted-foreground" data-testid={`${testId}-large-warning`}>
          {LARGE_PROJECT_IMAGE_WARNING}
        </p>
      )}
    </FieldRow>
  );
}

/* ─── Main page ────────────────────────────────────────────────────────── */
export default function ProposalWizardPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const templateId = params.get('templateId');
  const templateNameParam = params.get('templateName');
  const proposalCategory = params.get('category') || GRANT_PROPOSAL_CATEGORY;
  const rerunJobId = params.get('rerunJobId');
  const { user, api, refreshUser } = useAuth();

  const [step, setStep] = useState(1);
  const [data, setData] = useState(INITIAL_DATA);

  // Keep grant_items a stable array (draft/rerun restores may omit or corrupt it).
  useEffect(() => {
    if (!Array.isArray(data.grant_items) || data.grant_items.length < 4) {
      setData((d) => ({ ...d, grant_items: normalizeGrantItems(d.grant_items) }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [logoFile, setLogoFile] = useState(null);
  const [projectImageFile, setProjectImageFile] = useState(null);
  // Image source provenance — drives the friendly UX banners.
  // 'fresh' (no images yet) | 'restored-server' (from rerun/reuse) |
  // 'restored-draft' (from localStorage draft) | 'replaced' (user re-picked)
  const [imageSource, setImageSource] = useState('fresh');

  const [aiNarratives, setAiNarratives] = useState({});
  const [aiLoading, setAiLoading] = useState({});      // { section_key: bool }
  const [aiAllLoading, setAiAllLoading] = useState(false);
  const [swotLoading, setSwotLoading] = useState(false);
  const [grantLoading, setGrantLoading] = useState(false);

  const [generating, setGenerating] = useState(false);
  const [polling, setPolling] = useState(false);
  const [jobResult, setJobResult] = useState(null);

  const [templateMeta, setTemplateMeta] = useState(
    () => (templateId && templateNameParam
      ? { id: templateId, name: decodeURIComponent(templateNameParam) }
      : null),
  );
  const [credits, setCredits] = useState(5);

  const resolvedIndustry = useMemo(
    () => resolveIndustry(data.industry_sector, data.industry_other),
    [data.industry_sector, data.industry_other],
  );

  // Save-as-Draft (localStorage). The key includes templateId so different
  // proposals don't trample each other.
  const draftKey = templateId ? `proposal_draft_v1__${templateId}` : null;
  const [showDraftPill, setShowDraftPill] = useState(false);
  const draftRestoredRef = useRef(false);

  // ── Helpers: dataURI ⇄ File round-trip for image draft persistence ────
  const fileToDataURI = (file) => new Promise((resolve, reject) => {
    if (!file) { resolve(null); return; }
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  const dataURIToFile = (dataURI, filename) => {
    if (!dataURI || typeof dataURI !== 'string') return null;
    try {
      const [header, base64] = dataURI.split(',');
      const mime = (header.match(/data:(.+?);/) || [])[1] || 'image/png';
      const bin = atob(base64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      return new File([arr], filename, { type: mime });
    } catch { return null; }
  };

  // 1a) Server-side restore — when ?rerunJobId is present, fetch original
  //     job's input_data + image dataURIs and pre-fill the wizard. Bypasses
  //     localStorage draft since the user is explicitly re-using a job.
  const rerunRestoreRef = useRef(false);
  useEffect(() => {
    if (!rerunJobId || rerunRestoreRef.current) return;
    rerunRestoreRef.current = true;
    (async () => {
      try {
        const [rerunRes, logosRes] = await Promise.all([
          api.get(`/jobs/${rerunJobId}/rerun-data`),
          api.get(`/documents/${rerunJobId}/logos`).catch(() => ({ data: { logo: null, project_image: null } })),
        ]);
        const inputData = rerunRes.data?.input_data || {};
        // Strip null / undefined so INITIAL_DATA defaults remain.
        const clean = Object.fromEntries(Object.entries(inputData).filter(([, v]) => v !== null && v !== undefined));
        setData((d) => ({
          ...d,
          ...clean,
          ...normalizeIndustryFields(clean),
          grant_items: normalizeGrantItems(clean.grant_items ?? d.grant_items),
          risks: (clean.risks?.length ? clean.risks.map(normalizeGrantRisk) : d.risks),
        }));
        if (inputData.ai_narratives) {
          setAiNarratives(sanitizeAiNarratives(inputData.ai_narratives));
        }

        const { templateId: resolvedId, templateName, fallbackUsed } = resolveRerunTemplate(
          rerunRes.data,
        );
        if (resolvedId) {
          const tpl = (rerunRes.data?.available_templates || []).find((t) => t.id === resolvedId);
          if (tpl) setTemplateMeta(tpl);
          const current = new URLSearchParams(window.location.search);
          const needsUrlUpdate = current.get('templateId') !== resolvedId
            || (templateName && current.get('templateName') !== templateName);
          if (needsUrlUpdate) {
            const sp = applyRerunTemplateToSearchParams(current, {
              templateId: resolvedId,
              templateName,
            });
            navigate(`${window.location.pathname}?${sp.toString()}`, { replace: true });
          }
        }
        if (fallbackUsed) {
          toast.info(RERUN_TEMPLATE_FALLBACK_MESSAGE);
        }

        const lf = dataURIToFile(logosRes.data?.logo, 'restored-logo.png');
        const pf = dataURIToFile(logosRes.data?.project_image, 'restored-project-image.png');
        if (lf) setLogoFile(lf);
        if (pf) setProjectImageFile(pf);
        if (lf || pf) {
          setImageSource('restored-server');
          toast.success('Previously uploaded images restored from your earlier proposal.');
        } else {
          toast.info('Proposal data restored. Please re-upload your business logo and project image.');
        }
      } catch (e) {
        toast.error('Could not load previous proposal data — starting fresh.');
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rerunJobId]);

  // 1b) Restore from localStorage draft (only when not in a rerun flow).
  useEffect(() => {
    if (!draftKey) return;
    if (rerunJobId) return;  // server-side rerun takes precedence
    if (draftRestoredRef.current) return;  // StrictMode dedup
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.data) {
        setData((d) => ({
          ...d,
          ...parsed.data,
          ...normalizeIndustryFields(parsed.data),
          grant_items: normalizeGrantItems(parsed.data.grant_items ?? d.grant_items),
          risks: (parsed.data.risks?.length ? parsed.data.risks.map(normalizeGrantRisk) : d.risks),
        }));
      }
      if (parsed?.aiNarratives) setAiNarratives(sanitizeAiNarratives(parsed.aiNarratives));
      if (parsed?.step) setStep(parsed.step);
      // Image dataURIs (capped to keep localStorage under quota).
      const lf = dataURIToFile(parsed?.images?.logo, 'draft-logo.png');
      const pf = dataURIToFile(parsed?.images?.project_image, 'draft-project-image.png');
      if (lf) setLogoFile(lf);
      if (pf) setProjectImageFile(pf);
      if (lf || pf) setImageSource('restored-draft');
      draftRestoredRef.current = true;
      setShowDraftPill(true);
      toast.success('Draft restored — pick up where you left off.');
    } catch {
      // Corrupt draft — wipe it.
      try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey, rerunJobId]);

  // 2) Auto-save every 30s while the wizard is open. Includes images as
  //    dataURIs (skipped if combined size > 1.5 MB to avoid localStorage
  //    quota errors — quota is typically ~5 MB and we leave headroom).
  useEffect(() => {
    if (!draftKey) return;
    if (rerunJobId) return;  // never overwrite an in-flight server rerun
    const id = setInterval(async () => {
      try {
        let logoDataUri = null, projDataUri = null;
        if (logoFile && logoFile.size <= 1024 * 1024) logoDataUri = await fileToDataURI(logoFile);
        if (projectImageFile && projectImageFile.size <= 1024 * 1024) projDataUri = await fileToDataURI(projectImageFile);
        const payload = JSON.stringify({
          data, aiNarratives: sanitizeAiNarratives(aiNarratives), step,
          images: { logo: logoDataUri, project_image: projDataUri },
          saved_at: new Date().toISOString(),
        });
        // Last-resort guard: if payload exceeds 4 MB, drop images and re-save.
        if (payload.length > 4 * 1024 * 1024) {
          const slim = JSON.stringify({
            data,
            aiNarratives: sanitizeAiNarratives(aiNarratives),
            step,
            images: { logo: null, project_image: null },
            saved_at: new Date().toISOString(),
          });
          localStorage.setItem(draftKey, slim);
        } else {
          localStorage.setItem(draftKey, payload);
        }
      } catch {
        // localStorage may be full or disabled — fail silently.
      }
    }, 30000);
    return () => clearInterval(id);
  }, [draftKey, rerunJobId, data, aiNarratives, step, logoFile, projectImageFile]);

  // Step 8: strip legacy risk_and_mitigation narrative keys and stale loading flags.
  useEffect(() => {
    if (step !== 8) return;
    setAiNarratives((n) => sanitizeAiNarratives(n));
    setAiLoading((loading) => {
      if (!loading.risk_and_mitigation) return loading;
      const { risk_and_mitigation: _removed, ...rest } = loading;
      return rest;
    });
  }, [step]);

  const clearDraft = () => {
    if (!draftKey) return;
    try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
    setShowDraftPill(false);
    toast.info('Draft cleared.');
  };
  const dismissDraftPill = () => setShowDraftPill(false);

  // Wrap the file setters so user replacement clears the "restored" state
  // and the inline notice auto-disappears.
  const handleLogoSet = useCallback((f) => {
    setLogoFile(f);
    if (f) setImageSource('replaced');
  }, []);
  const handleProjectImageSet = useCallback((f) => {
    setProjectImageFile(f);
    if (f) setImageSource('replaced');
  }, []);

  /* ── Bootstrap: fetch template meta + per-doc credits ── */
  useEffect(() => {
    if (!templateId) {
      // Wait for the rerun useEffect to inject the original templateId
      // when restoring from a previous job. If neither is present, redirect
      // the user back to the template picker.
      if (rerunJobId) return;
      toast.error('No template selected. Pick a template first.');
      navigate(`/documents/business-proposal/templates?category=${GRANT_PROPOSAL_CATEGORY}`);
      return;
    }
    (async () => {
      try {
        const [tplRes, typesRes] = await Promise.all([
          api.get(`/templates/by-type/${proposalCategory}`, { params: { category: proposalCategory } }),
          api.get('/documents/types'),
        ]);
        const templates = tplRes.data?.templates || [];
        let tpl = templates.find((t) => t.id === templateId);
        if (!tpl && templates.length > 0 && rerunJobId) {
          tpl = templates[0];
          const sp = applyRerunTemplateToSearchParams(window.location.search, {
            templateId: tpl.id,
            templateName: tpl.name,
          });
          navigate(`${window.location.pathname}?${sp.toString()}`, { replace: true });
          toast.info(RERUN_TEMPLATE_FALLBACK_MESSAGE);
        }
        if (tpl) {
          setTemplateMeta(tpl);
        } else if (templateNameParam) {
          setTemplateMeta({ id: templateId, name: decodeURIComponent(templateNameParam) });
        }
        const meta = (typesRes.data?.types || []).find(
          (t) => t.id === 'business_proposal' || t.id === GRANT_PROPOSAL_CATEGORY,
        );
        if (meta?.credits) setCredits(meta.credits);
      } catch (err) {
        // Non-blocking — URL templateName fallback already applied.
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, templateNameParam, proposalCategory, rerunJobId]);

  /* ── Form mutators ── */
  const setField = (key, val) => setData((d) => ({ ...d, [key]: val }));
  const setRowField = (listKey, idx, key, val) =>
    setData((d) => ({ ...d, [listKey]: d[listKey].map((row, i) => (i === idx ? { ...row, [key]: val } : row)) }));
  const addRow = (listKey, blank) =>
    setData((d) => ({ ...d, [listKey]: [...d[listKey], { ...blank }] }));
  const removeRow = (listKey, idx) =>
    setData((d) => ({ ...d, [listKey]: d[listKey].filter((_, i) => i !== idx) }));

  const setSwot = (cat, idx, val) =>
    setData((d) => ({ ...d, swot: { ...d.swot, [cat]: d.swot[cat].map((v, i) => (i === idx ? val : v)) } }));
  const addSwot = (cat) =>
    setData((d) => ({ ...d, swot: { ...d.swot, [cat]: [...d.swot[cat], ''] } }));
  const removeSwot = (cat, idx) =>
    setData((d) => ({ ...d, swot: { ...d.swot, [cat]: d.swot[cat].filter((_, i) => i !== idx) } }));

  /* ── Step validation ── */
  const grantRowsForSubmit = useCallback(
    () => (Array.isArray(data.grant_items) ? data.grant_items : [])
      .filter((g) => (g.item || '').trim() && Number(stripCommas(g.amount)) > 0)
      .map((g) => ({
        item: (g.item || '').trim(),
        purpose: (g.purpose || '').trim(),
        amount: Number(stripCommas(g.amount)) || 0,
      })),
    [data.grant_items],
  );

  const validate = (s) => {
    if (s === 1) {
      const funding = Number(stripCommas(data.funding_amount)) || 0;
      if (!funding) return 'Enter the funding amount you are requesting.';
      if (!data.business_name.trim()) return 'Business name is required.';
      if (!data.industry_sector) return 'Industry is required.';
      if (data.industry_sector === INDUSTRY_OTHER && !data.industry_other.trim()) {
        return 'Please specify your industry.';
      }
    }
    if (s === 2) {
      if (!data.business_goals.trim()) return 'Add at least your top business goal.';
    }
    if (s === 3) {
      if (!grantRowsForSubmit().length) {
        return 'Add at least one Grant Utilization row with a category name and amount, or use Get Suggestions.';
      }
    }
    if (s === 4) {
      if (!data.products.length || !data.products.some((p) => p.name.trim())) {
        return 'Add at least one product or service.';
      }
    }
    if (s === 5) {
      if (!data.operating_expenses.length || !data.operating_expenses.some((x) => x.name.trim())) {
        return 'Add at least one operating expense.';
      }
    }
    if (s === 6) {
      if (!data.management_team.length || !data.management_team.some((t) => t.role.trim())) {
        return 'Add at least one team role.';
      }
    }
    if (s === 7) {
      if (!completedRiskRows(data.risks).length) {
        return 'Add at least one risk with a Risk Factor and Mitigation Strategy.';
      }
    }
    return null;
  };

  const goNext = () => {
    const err = validate(step);
    if (err) {
      toast.error(err);
      return;
    }
    setStep((s) => Math.min(WIZARD_STEP_COUNT, s + 1));
  };
  const goBack = () => setStep((s) => Math.max(1, s - 1));

  /* ── Build the AI context payload (shared by section regeneration) ── */
  const aiPayload = useMemo(
    () => ({
      business_name: data.business_name,
      grant_body: data.grant_body,
      industry: resolvedIndustry,
      nature_of_business: data.nature_of_business,
      business_stage: data.business_stage,
      target_market: data.target_market,
      business_goals: data.business_goals,
      funding_purpose: data.funding_purpose,
      proposal_type: data.proposal_type,
      currency: data.currency,
      products: data.products
        .filter((p) => p.name.trim())
        .map((p) => ({
          name: p.name,
          description: p.description,
          cost_price: Number(stripCommas(p.cost_price)) || 0,
          selling_price: Number(stripCommas(p.selling_price)) || 0,
          monthly_qty: Number(stripCommas(p.monthly_qty)) || 0,
        })),
      operating_expenses: data.operating_expenses
        .filter((x) => x.name.trim())
        .map((x) => ({ name: x.name, monthly_cost: Number(stripCommas(x.monthly_cost)) || 0 })),
      swot: data.swot,
      funding_amount: Number(stripCommas(data.funding_amount)) || 0,
      grant_items: grantRowsForSubmit(),
      risks: completedRiskRows(data.risks).map((r) => ({
          risk_name: r.risk_name,
          risk_level: r.risk_level || 'Medium',
          risk_mitigation: r.risk_mitigation,
        })),
    }),
    [data, grantRowsForSubmit, resolvedIndustry],
  );

  /* ── AI: generate one section ── */
  const generateOneSection = useCallback(async (section_key) => {
    if (!NARRATIVE_SECTION_KEY_SET.has(section_key)) return;
    setAiLoading((s) => ({ ...s, [section_key]: true }));
    try {
      const res = await api.post('/documents/ai-proposal-section', { ...aiPayload, section: section_key });
      const text = res.data?.text || '';
      if (!text) throw new Error('Empty response');
      setAiNarratives((n) => ({ ...n, [section_key]: text }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not generate section. Please retry.');
    } finally {
      setAiLoading((s) => ({ ...s, [section_key]: false }));
    }
  }, [api, aiPayload]);

  /* ── AI: generate ALL sections in parallel (entry to step 6) ── */
  const generateAllSections = useCallback(async () => {
    setAiAllLoading(true);
    try {
      await Promise.all(AI_NARRATIVE_SECTIONS.map(({ key }) => generateOneSection(key)));
      toast.success('Narrative drafted — review and edit before generating.');
    } finally {
      setAiAllLoading(false);
    }
  }, [generateOneSection]);

  /* ── AI: generate SWOT suggestions ── */
  const [contextLoading, setContextLoading] = useState(false);

  const generateContextSuggestions = useCallback(async () => {
    if (contextLoading) return;
    if (!data.nature_of_business.trim() && !resolvedIndustry.trim()) {
      toast.error('Add the Nature of Business or Industry first so suggestions have context.');
      return;
    }
    setContextLoading(true);
    try {
      const res = await api.post('/documents/ai-proposal-context', {
        business_name: data.business_name,
        industry: resolvedIndustry,
        nature_of_business: data.nature_of_business,
        business_address: data.business_address,
        target_market: data.target_market,
        business_goals: data.business_goals,
        funding_amount: Number(stripCommas(data.funding_amount)) || 0,
        currency: data.currency,
        proposal_type: data.proposal_type,
        products: data.products
          .filter((p) => p.name.trim())
          .map((p) => ({ name: p.name, description: p.description })),
      });
      const ctx = res.data?.context || {};
      // Merge — only fill empty fields, don't overwrite user edits.
      setData((d) => ({
        ...d,
        business_goals: d.business_goals.trim() || ctx.business_goals || '',
        funding_purpose: d.funding_purpose.trim() || ctx.funding_purpose || '',
        target_market: d.target_market.trim() || ctx.target_market || '',
        business_stage: d.business_stage.trim() || ctx.business_stage || '',
        problem_solved: d.problem_solved.trim() || ctx.problem_solved || '',
        competitive_positioning: d.competitive_positioning.trim() || ctx.competitive_positioning || '',
      }));
      toast.success('Context drafted — edit any field before continuing.');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not generate suggestions. Please retry.';
      toast.error(detail);
    } finally {
      setContextLoading(false);
    }
  }, [api, data, contextLoading, resolvedIndustry]);

  const generateSwotSuggestions = useCallback(async () => {
    if (swotLoading) return;
    if (!data.business_name.trim() && !resolvedIndustry.trim() && !data.nature_of_business.trim()) {
      toast.error('Add a business name, industry, or nature of business first so suggestions have context.');
      return;
    }
    setSwotLoading(true);
    try {
      const res = await api.post('/documents/ai-proposal-swot', {
        business_name: data.business_name,
        industry: resolvedIndustry,
        nature_of_business: data.nature_of_business,
        business_stage: data.business_stage,
        target_market: data.target_market,
        business_goals: data.business_goals,
        funding_purpose: data.funding_purpose,
        proposal_type: data.proposal_type,
        funding_amount: Number(stripCommas(data.funding_amount)) || 0,
        currency: data.currency,
        products: data.products
          .filter((p) => p.name.trim())
          .map((p) => ({ name: p.name, description: p.description })),
      });
      const swot = res.data?.swot || {};
      const empty = ['strengths','weaknesses','opportunities','threats']
        .every((k) => !(swot[k] || []).length);
      if (empty) {
        toast.error('No suggestions returned. Try refining your business description.');
        return;
      }
      setData((d) => ({
        ...d,
        swot: {
          strengths: (swot.strengths || []).map(String).slice(0, 3),
          weaknesses: (swot.weaknesses || []).map(String).slice(0, 3),
          opportunities: (swot.opportunities || []).map(String).slice(0, 3),
          threats: (swot.threats || []).map(String).slice(0, 3),
        },
      }));
      toast.success('SWOT suggestions applied — edit any item before generating.');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not suggest SWOT. Please retry.';
      toast.error(detail);
    } finally {
      setSwotLoading(false);
    }
  }, [api, data, swotLoading, resolvedIndustry]);

  const generateGrantUtilSuggestions = useCallback(async () => {
    if (grantLoading) return;
    const funding = Number(stripCommas(data.funding_amount)) || 0;
    if (!funding) {
      toast.error('Enter the funding amount on Step 1 before generating a utilization plan.');
      return;
    }
    if (!data.nature_of_business.trim() && !resolvedIndustry.trim()) {
      toast.error('Add the Nature of Business or Industry first so suggestions have context.');
      return;
    }
    setGrantLoading(true);
    try {
      const res = await api.post('/documents/ai-proposal-grant', {
        business_name: data.business_name,
        industry: resolvedIndustry,
        nature_of_business: data.nature_of_business,
        business_stage: data.business_stage,
        target_market: data.target_market,
        business_goals: data.business_goals,
        funding_purpose: data.funding_purpose,
        proposal_type: data.proposal_type,
        funding_amount: funding,
        currency: data.currency,
        products: data.products
          .filter((p) => p.name.trim())
          .map((p) => ({ name: p.name, description: p.description })),
      });
      const items = res.data?.grant_items || [];
      if (!items.length) {
        toast.error('No utilization rows returned. Please retry.');
        return;
      }
      setData((d) => ({
        ...d,
        grant_items: normalizeGrantItems(
          items.map((g) => ({
            item: g.item || '',
            purpose: g.purpose || '',
            amount: g.amount != null ? String(g.amount) : '',
          })),
        ),
      }));
      toast.success('Grant utilization plan drafted — edit any row before generating.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not generate utilization plan. Please retry.');
    } finally {
      setGrantLoading(false);
    }
  }, [api, data, grantLoading, resolvedIndustry]);

  const handleRiskMitigationSuggest = useCallback(async (idx) => {
    const risk = data.risks[idx];
    const riskName = (risk?.risk_name || '').trim();
    if (!riskName) {
      toast.error('Enter a risk name before requesting a mitigation suggestion.');
      return;
    }
    setAiLoading((s) => ({ ...s, [`risk_${idx}`]: true }));
    try {
      const res = await api.post('/documents/business-plan/draft-section', {
        section: 'risk_mitigation',
        risk_name: riskName,
        risk_likelihood: risk?.risk_level || 'Medium',
        risk_impact: risk?.risk_level || 'Medium',
        industry: resolvedIndustry,
        business_name: data.business_name,
        funding_purpose: data.funding_purpose,
      });
      setRowField('risks', idx, 'risk_mitigation', res.data?.risk_mitigation || '');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not generate mitigation suggestion.');
    } finally {
      setAiLoading((s) => ({ ...s, [`risk_${idx}`]: false }));
    }
  }, [api, data.business_name, data.funding_purpose, data.risks, resolvedIndustry]);

  /* ── Generate proposal (final action) ── */
  const handleFinalGenerate = async () => {
    if (generating) return;
    const grantRows = grantRowsForSubmit();
    if (!grantRows.length) {
      toast.error('Complete the Grant Utilization Plan (Step 3) before generating.');
      setStep(3);
      return;
    }
    setGenerating(true);
    setJobResult(null);
    try {
      const inputData = {
        ...aiPayload,
        business_address: data.business_address,
        business_email: data.business_email,
        business_phone: data.business_phone,
        business_website: data.business_website,
        grant_body: data.grant_body,
        management_team: data.management_team
          .filter((t) => t.role.trim())
          .map((t) => ({ name: t.name, role: t.role, responsibility: t.responsibility })),
        risks: completedRiskRows(data.risks).map((r) => ({
            risk_name: r.risk_name,
            risk_level: r.risk_level || 'Medium',
            risk_mitigation: r.risk_mitigation,
          })),
        ai_narratives: sanitizeAiNarratives(aiNarratives),
        grant_items: grantRows,
      };

      let res;
      if (logoFile || projectImageFile) {
        const fd = new FormData();
        fd.append('service_type', 'document_generation');
        fd.append('document_type', GRANT_PROPOSAL_CATEGORY);
        fd.append('template_id', templateId);
        fd.append('input_data', JSON.stringify(inputData));
        if (logoFile) fd.append('logo', logoFile);
        if (projectImageFile) fd.append('project_image', projectImageFile);
        res = await api.post('/documents/create-with-logo', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        res = await api.post('/documents/create', {
          service_type: 'document_generation',
          document_type: GRANT_PROPOSAL_CATEGORY,
          template_id: templateId,
          input_data: inputData,
        });
      }
      toast.success('Generation started — this may take 20-30 seconds.');
      pollJob(res.data.job_id);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || 'Generation failed';
      if (status === 402) {
        toast.error('Insufficient credits. Add credits and try again.');
      } else {
        toast.error(detail);
      }
      setGenerating(false);
    }
  };

  const pollJob = async (jobId) => {
    setPolling(true);
    for (let i = 0; i < 90; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const res = await api.get(`/jobs/${jobId}`);
        if (res.data.status === 'preview_ready' || res.data.status === 'completed') {
          setJobResult(res.data);
          setPolling(false);
          setGenerating(false);
          refreshUser?.();
          // Clear local draft once the proposal is officially generated — the
          // user has a real artifact in the system now.
          if (draftKey) {
            try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
            setShowDraftPill(false);
          }
          toast.success('Your proposal is ready.');
          scrollToResultCard();
          return;
        }
        if (res.data.status === 'failed') {
          setJobResult(res.data);
          setPolling(false);
          setGenerating(false);
          toast.error(res.data.error_message || 'Generation failed');
          return;
        }
      } catch {
        // network blip — keep polling
      }
    }
    setPolling(false);
    setGenerating(false);
    toast.error('Generation timed out. Please try again.');
  };

  const handleDownload = async () => {
    if (!jobResult) return;
    let currentJob = jobResult;
    if (currentJob.status === 'preview_ready') {
      try {
        const fin = await api.post(`/documents/${currentJob.id}/finalize-download`);
        if (fin.data?.balance != null) {
          currentJob = { ...currentJob, status: 'completed' };
          setJobResult(currentJob);
        }
      } catch (err) {
        const status = err.response?.status;
        if (status === 402) {
          toast.error('Not enough credits.');
          navigate('/credits');
        } else {
          toast.error(err.response?.data?.detail || 'Could not unlock document.');
        }
        return;
      }
    }
    try {
      const res = await api.get(`/storage/download/${currentJob.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = currentJob.metadata?.filename || 'business-proposal.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Download failed.');
    }
  };

  /* ── Step renderers ── */
  const renderStep1 = () => (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="md:col-span-2">
        <FieldRow
          label={`Funding Amount Requested (${data.currency})`}
          required
          hint="Total funding you are requesting — used in narratives and the grant utilization plan"
        >
          <Input
            value={data.funding_amount}
            onChange={(e) => setField('funding_amount', e.target.value)}
            placeholder="10000000"
            data-testid="field-funding_amount"
          />
        </FieldRow>
      </div>
      <FieldRow label="Business Name" required>
        <Input value={data.business_name} onChange={(e) => setField('business_name', e.target.value)} placeholder="Northwind Health" data-testid="field-business_name" />
      </FieldRow>
      <FieldRow label="Grant Body / Submitted To" hint="Organisation or agency receiving this proposal">
        <Input value={data.grant_body} onChange={(e) => setField('grant_body', e.target.value)} placeholder="Federal Ministry of Industry" data-testid="field-grant_body" />
      </FieldRow>
      <FieldRow label="Industry / Sector" required>
        <IndustrySelector
          sector={data.industry_sector}
          other={data.industry_other}
          onSectorChange={(v) => setField('industry_sector', v)}
          onOtherChange={(v) => setField('industry_other', v)}
        />
      </FieldRow>
      <FieldRow label="Nature of Business" hint="One sentence describing what you do">
        <Input value={data.nature_of_business} onChange={(e) => setField('nature_of_business', e.target.value)} data-testid="field-nature_of_business" />
      </FieldRow>
      <FieldRow label="Business Address">
        <Input value={data.business_address} onChange={(e) => setField('business_address', e.target.value)} data-testid="field-business_address" />
      </FieldRow>
      <FieldRow label="Business Phone">
        <Input value={data.business_phone} onChange={(e) => setField('business_phone', e.target.value)} data-testid="field-business_phone" />
      </FieldRow>
      <FieldRow label="Business Email">
        <Input type="email" value={data.business_email} onChange={(e) => setField('business_email', e.target.value)} data-testid="field-business_email" />
      </FieldRow>
      <FieldRow label="Website">
        <Input value={data.business_website} onChange={(e) => setField('business_website', e.target.value)} data-testid="field-business_website" />
      </FieldRow>
      <FieldRow label="Currency">
        <CurrencySelector value={data.currency} onChange={(v) => setField('currency', v)} />
      </FieldRow>
      <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6 pt-2">
        <ImagePicker
          label="Company Logo"
          file={logoFile} setFile={handleLogoSet}
          hint="Optional — upload your business logo."
          testId="upload-logo"
        />
        <ImagePicker
          label="Project Image"
          file={projectImageFile} setFile={handleProjectImageSet}
          hint="Optional — upload an image related to your business or project."
          testId="upload-project-image"
          warnIfLargerThan={LARGE_PROJECT_IMAGE_BYTES}
        />
        {imageSource === 'restored-server' && (
          <div className="sm:col-span-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900" data-testid="images-restored-server">
            <strong>Images restored</strong> from your earlier proposal. You can keep, replace, or remove them before generating.
          </div>
        )}
        {imageSource === 'restored-draft' && (
          <div className="sm:col-span-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900" data-testid="images-restored-draft">
            <strong>Images restored</strong> from your saved draft. They'll be used unless you replace them below.
          </div>
        )}
        {(rerunJobId || draftRestoredRef.current) && imageSource === 'fresh' && !logoFile && !projectImageFile && (
          <div className="sm:col-span-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="images-needed-notice">
            Previously uploaded images couldn't be retained. Please re-upload your business logo and project image before generating.
          </div>
        )}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="md:col-span-2 flex items-start justify-between gap-3 rounded-lg border border-primary/20 bg-primary/5 p-3">
        <div className="text-sm">
          <div className="font-medium text-foreground">Need help filling this out?</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Auto-draft Business Goals, Funding Purpose, Target Market, Business Stage, Problem Solved, and Competitive Positioning from your Nature of Business and Industry. Suggestions stay editable.
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={generateContextSuggestions}
          disabled={contextLoading}
          data-testid="ai-suggest-context-btn"
          className="shrink-0"
        >
          {contextLoading
            ? <><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Drafting…</>
            : <><Sparkles className="mr-1 h-3 w-3" /> Get Suggestions</>}
        </Button>
      </div>
      <FieldRow label="Business Goals" required hint="Top 1-2 goals for the next 12-18 months">
        <Textarea rows={3} value={data.business_goals} onChange={(e) => setField('business_goals', e.target.value)} data-testid="field-business_goals" />
      </FieldRow>
      <FieldRow label="Funding Purpose" hint="What will the funding be used for?">
        <Textarea rows={3} value={data.funding_purpose} onChange={(e) => setField('funding_purpose', e.target.value)} data-testid="field-funding_purpose" />
      </FieldRow>
      <FieldRow label="Target Market" hint="Who are your primary customers?">
        <Textarea rows={2} value={data.target_market} onChange={(e) => setField('target_market', e.target.value)} data-testid="field-target_market" />
      </FieldRow>
      <FieldRow label="Business Stage" hint="Idea • Early growth • Established • Scale-up">
        <Input value={data.business_stage} onChange={(e) => setField('business_stage', e.target.value)} data-testid="field-business_stage" />
      </FieldRow>
      <FieldRow label="Problem Being Solved" hint="What pain do customers have today?">
        <Textarea rows={2} value={data.problem_solved} onChange={(e) => setField('problem_solved', e.target.value)} data-testid="field-problem_solved" />
      </FieldRow>
      <FieldRow label="Competitive Positioning" hint="How are you different from alternatives?">
        <Textarea rows={2} value={data.competitive_positioning} onChange={(e) => setField('competitive_positioning', e.target.value)} data-testid="field-competitive_positioning" />
      </FieldRow>
      <div className="md:col-span-2 rounded-lg border bg-muted/30 p-4">
        <div className="mb-2 flex items-center justify-between gap-2 flex-wrap">
          <div className="text-sm font-medium">SWOT Analysis (optional)</div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={generateSwotSuggestions}
            disabled={swotLoading}
            data-testid="ai-suggest-swot-btn"
          >
            {swotLoading
              ? <><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Suggesting…</>
              : <><Sparkles className="mr-1 h-3 w-3" /> Get Suggestions</>}
          </Button>
        </div>
        <p className="mb-3 text-xs text-muted-foreground">
          Suggestions use your industry, products, target market and goals to suggest tailored bullets.
          You can edit, replace, or delete any item before generating the proposal.
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          {['strengths', 'weaknesses', 'opportunities', 'threats'].map((cat) => (
            <div key={cat} className="space-y-2">
              <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{cat}</div>
              {data.swot[cat].map((v, i) => (
                <div key={i} className="flex gap-1">
                  <Input
                    value={v}
                    onChange={(e) => setSwot(cat, i, e.target.value)}
                    placeholder={`Add a ${cat.replace(/s$/, '')}…`}
                    data-testid={`field-swot-${cat}-${i}`}
                  />
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeSwot(cat, i)} className="text-muted-foreground hover:text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => addSwot(cat)} className="w-full" data-testid={`field-swot-${cat}-add`}>
                <Plus className="mr-1 h-3 w-3" /> Add
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const grantTableRows = Array.isArray(data.grant_items) ? data.grant_items : defaultGrantRows();

  const renderStep3 = () => (
    <div className="space-y-4" data-testid="grant-util-step">
      <p className="text-sm text-muted-foreground">
        Break down how the requested funding will be used. Get suggestions for categories, then edit any row before generating your proposal.
      </p>
      <div className="rounded-lg border border-primary/25 bg-primary/5 p-4 space-y-3" data-testid="grant-util-section">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-sm font-semibold text-foreground">Grant Utilization Plan</div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Total requested: {data.currency}{' '}
              {data.funding_amount ? stripCommas(data.funding_amount).replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '—'}
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={generateGrantUtilSuggestions}
            disabled={grantLoading}
            data-testid="ai-suggest-grant-btn"
          >
            {grantLoading
              ? <><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Drafting…</>
              : <><Sparkles className="mr-1 h-3 w-3" /> Get Suggestions</>}
          </Button>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[22%]">Category</TableHead>
              <TableHead>Description / purpose</TableHead>
              <TableHead className="w-[18%]">Amount ({data.currency})</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {grantTableRows.map((g, idx) => (
              <TableRow key={idx}>
                <TableCell className="align-top py-2">
                  <Input
                    value={g.item}
                    onChange={(e) => setRowField('grant_items', idx, 'item', e.target.value)}
                    placeholder="Kitchen equipment"
                    data-testid={`field-grant-${idx}-item`}
                  />
                </TableCell>
                <TableCell className="align-top py-2">
                  <Textarea
                    rows={2}
                    value={g.purpose}
                    onChange={(e) => setRowField('grant_items', idx, 'purpose', e.target.value)}
                    placeholder="Commercial ovens, prep stations, and cold storage"
                    data-testid={`field-grant-${idx}-purpose`}
                  />
                </TableCell>
                <TableCell className="align-top py-2">
                  <Input
                    value={g.amount}
                    onChange={(e) => setRowField('grant_items', idx, 'amount', e.target.value)}
                    placeholder="2500000"
                    data-testid={`field-grant-${idx}-amount`}
                  />
                </TableCell>
                <TableCell className="align-top py-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeRow('grant_items', idx)}
                    disabled={grantTableRows.length <= 1}
                    className="text-muted-foreground hover:text-destructive"
                    data-testid={`field-grant-${idx}-remove`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => addRow('grant_items', BLANK_GRANT)}
          data-testid="add-grant-row-btn"
        >
          <Plus className="mr-1 h-3 w-3" /> Add Row
        </Button>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">List the products or services you sell. This is used for revenue & profit calculations.</p>
      {data.products.map((p, idx) => (
        <RepeatableRow key={idx} removable={data.products.length > 1} onRemove={() => removeRow('products', idx)}>
          <div className="grid gap-3 md:grid-cols-2">
            <FieldRow label="Name">
              <Input value={p.name} onChange={(e) => setRowField('products', idx, 'name', e.target.value)} placeholder="Group Fitness Membership" data-testid={`field-products-${idx}-name`} />
            </FieldRow>
            <FieldRow label="Description">
              <Input value={p.description} onChange={(e) => setRowField('products', idx, 'description', e.target.value)} placeholder="Unlimited monthly access" data-testid={`field-products-${idx}-description`} />
            </FieldRow>
            <FieldRow label={`Cost Price (${data.currency})`} hint="Per-unit cost to deliver">
              <Input value={p.cost_price} onChange={(e) => setRowField('products', idx, 'cost_price', e.target.value)} placeholder="28" data-testid={`field-products-${idx}-cost_price`} />
            </FieldRow>
            <FieldRow label={`Selling Price (${data.currency})`}>
              <Input value={p.selling_price} onChange={(e) => setRowField('products', idx, 'selling_price', e.target.value)} placeholder="119" data-testid={`field-products-${idx}-selling_price`} />
            </FieldRow>
            <FieldRow label="Expected Monthly Quantity">
              <Input value={p.monthly_qty} onChange={(e) => setRowField('products', idx, 'monthly_qty', e.target.value)} placeholder="240" data-testid={`field-products-${idx}-monthly_qty`} />
            </FieldRow>
          </div>
        </RepeatableRow>
      ))}
      <Button type="button" variant="outline" onClick={() => addRow('products', BLANK_PRODUCT)} data-testid="add-product-btn">
        <Plus className="mr-1 h-4 w-4" /> Add product / service
      </Button>
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">List your monthly running costs — rent, salaries, marketing, utilities, etc.</p>
      {data.operating_expenses.map((x, idx) => (
        <RepeatableRow key={idx} removable={data.operating_expenses.length > 1} onRemove={() => removeRow('operating_expenses', idx)}>
          <div className="grid gap-3 md:grid-cols-2">
            <FieldRow label="Expense Name">
              <Input value={x.name} onChange={(e) => setRowField('operating_expenses', idx, 'name', e.target.value)} placeholder="Rent and utilities" data-testid={`field-expense-${idx}-name`} />
            </FieldRow>
            <FieldRow label={`Monthly Cost (${data.currency})`}>
              <Input value={x.monthly_cost} onChange={(e) => setRowField('operating_expenses', idx, 'monthly_cost', e.target.value)} placeholder="8500" data-testid={`field-expense-${idx}-monthly_cost`} />
            </FieldRow>
          </div>
        </RepeatableRow>
      ))}
      <Button type="button" variant="outline" onClick={() => addRow('operating_expenses', BLANK_EXPENSE)} data-testid="add-expense-btn">
        <Plus className="mr-1 h-4 w-4" /> Add expense
      </Button>
    </div>
  );

  const renderStep6 = () => (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">Add the people running the business — founders, key managers, advisors.</p>
      {data.management_team.map((m, idx) => (
        <RepeatableRow key={idx} removable={data.management_team.length > 1} onRemove={() => removeRow('management_team', idx)}>
          <div className="grid gap-3 md:grid-cols-3">
            <FieldRow label="Name">
              <Input value={m.name} onChange={(e) => setRowField('management_team', idx, 'name', e.target.value)} placeholder="e.g. John Adeyemi" data-testid={`field-team-${idx}-name`} />
            </FieldRow>
            <FieldRow label="Role">
              <Input value={m.role} onChange={(e) => setRowField('management_team', idx, 'role', e.target.value)} placeholder="Chief Executive Officer" data-testid={`field-team-${idx}-role`} />
            </FieldRow>
            <FieldRow label="Responsibility">
              <Input value={m.responsibility} onChange={(e) => setRowField('management_team', idx, 'responsibility', e.target.value)} placeholder="Vision, fundraising, member experience" data-testid={`field-team-${idx}-responsibility`} />
            </FieldRow>
          </div>
        </RepeatableRow>
      ))}
      <Button type="button" variant="outline" onClick={() => addRow('management_team', BLANK_TEAMER)} data-testid="add-team-btn">
        <Plus className="mr-1 h-4 w-4" /> Add team member
      </Button>
    </div>
  );

  const renderStep7 = () => (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Identify key risks and mitigation strategies. Select a Risk Level for each row — this appears in your proposal table.
      </p>
      {data.risks.map((risk, idx) => (
        <RepeatableRow key={idx} removable={data.risks.length > 1} onRemove={() => removeRow('risks', idx)}>
          <div className="grid gap-3 md:grid-cols-2">
            <FieldRow label="Risk Factor" required>
              <Input
                value={risk.risk_name ?? ''}
                onChange={(e) => setRowField('risks', idx, 'risk_name', e.target.value)}
                placeholder="e.g. Supply chain disruption"
                data-testid={`field-risk-${idx}-name`}
              />
            </FieldRow>
            <FieldRow label="Risk Level">
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={risk.risk_level ?? 'Medium'}
                onChange={(e) => setRowField('risks', idx, 'risk_level', e.target.value)}
                data-testid={`field-risk-${idx}-level`}
              >
                {RISK_LEVELS.map((level) => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </FieldRow>
          </div>
          <FieldRow label="Mitigation Strategy" required>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
              <Textarea
                rows={3}
                className="flex-1"
                value={risk.risk_mitigation ?? ''}
                onChange={(e) => setRowField('risks', idx, 'risk_mitigation', e.target.value)}
                placeholder="How will you reduce or manage this risk?"
                data-testid={`field-risk-${idx}-mitigation`}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={() => handleRiskMitigationSuggest(idx)}
                disabled={aiLoading[`risk_${idx}`]}
                data-testid={`risk-mitigation-suggest-${idx}`}
              >
                {aiLoading[`risk_${idx}`]
                  ? <><Loader2 className="mr-1 h-3 w-3 animate-spin" /> …</>
                  : <><Sparkles className="mr-1 h-3 w-3" /> Get Suggestions</>}
              </Button>
            </div>
          </FieldRow>
        </RepeatableRow>
      ))}
      <Button type="button" variant="outline" onClick={() => addRow('risks', BLANK_RISK)} data-testid="add-risk-btn">
        <Plus className="mr-1 h-4 w-4" /> Add risk
      </Button>
    </div>
  );

  const renderStep8 = () => {
    const riskRows = completedRiskRows(data.risks);
    const hasNarrative = AI_NARRATIVE_SECTIONS.some(({ key }) => aiNarratives[key]);
    return (
      <div className="space-y-4">
        <div className="rounded-lg border bg-primary/5 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Sparkles className="h-5 w-5 text-primary" />
              <div>
                <div className="text-sm font-semibold">Ready-made narrative</div>
                <div className="text-xs text-muted-foreground">
                  We&apos;ve written the 6 long-form sections from your business inputs. Edit any section directly,
                  or click &quot;Regenerate&quot; to get a fresh version. Risk &amp; Mitigation is a read-only preview of Step 7.
                </div>
              </div>
            </div>
            <Button onClick={generateAllSections} disabled={aiAllLoading} data-testid="ai-generate-all-btn">
              {aiAllLoading ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Drafting…</> : <><Sparkles className="mr-1 h-4 w-4" /> {hasNarrative ? 'Regenerate all sections' : 'Generate all sections'}</>}
            </Button>
          </div>
        </div>
        <div className="space-y-3">
          <Card data-testid={`ai-section-${RISK_PREVIEW_SECTION.key}`}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{RISK_PREVIEW_SECTION.label}</CardTitle>
              <CardDescription>
                Read-only preview of your Step 7 entries — the proposal table is built from these rows.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Risk Factor</TableHead>
                    <TableHead className="w-28">Risk Level</TableHead>
                    <TableHead>Mitigation Strategy</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {riskRows.map((risk, idx) => (
                    <TableRow key={idx} data-testid={`risk-preview-row-${idx}`}>
                      <TableCell className="font-medium align-top">{risk.risk_name}</TableCell>
                      <TableCell className="align-top">{risk.risk_level}</TableCell>
                      <TableCell className="align-top whitespace-pre-wrap text-muted-foreground">
                        {risk.risk_mitigation}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          {AI_NARRATIVE_SECTIONS.map(({ key, label }) => (
            <Card key={key} data-testid={`ai-section-${key}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-base">{label}</CardTitle>
                  <Button
                    type="button" variant="outline" size="sm"
                    onClick={() => generateOneSection(key)}
                    disabled={aiLoading[key]}
                    data-testid={`regen-${key}`}
                  >
                    {aiLoading[key]
                      ? <><Loader2 className="mr-1 h-3 w-3 animate-spin" /> …</>
                      : <><RefreshCw className="mr-1 h-3 w-3" /> Regenerate</>}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Textarea
                  rows={6}
                  value={aiNarratives[key] || ''}
                  onChange={(e) => setAiNarratives((n) => ({ ...n, [key]: e.target.value }))}
                  placeholder={`Click "Regenerate" to draft ${label.toLowerCase()}, or write your own here.`}
                  data-testid={`textarea-${key}`}
                />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  };

  const renderStep9 = () => {
    const productCount = data.products.filter((p) => p.name.trim()).length;
    const grantCount = grantRowsForSubmit().length;
    const expenseCount = data.operating_expenses.filter((x) => x.name.trim()).length;
    const teamCount = data.management_team.filter((t) => t.role.trim()).length;
    const riskCount = completedRiskRows(data.risks).length;
    const narrativeCount = AI_NARRATIVE_SECTIONS.filter(({ key }) => (aiNarratives[key] || '').trim()).length;
    const sectionsReady = narrativeCount + (riskCount > 0 ? 1 : 0);
    const totalSections = AI_NARRATIVE_SECTIONS.length + 1;

    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Review your proposal</CardTitle>
            <CardDescription>Confirm everything is in order, then generate.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            <SummaryRow label="Business" value={data.business_name || '—'} />
            <SummaryRow label="Proposal type" value={data.proposal_type} />
            <SummaryRow label="Industry" value={resolvedIndustry || '—'} />
            <SummaryRow label="Currency" value={data.currency} />
            <SummaryRow label="Products / services" value={`${productCount}`} />
            <SummaryRow label="Operating expenses" value={`${expenseCount}`} />
            <SummaryRow label="Team" value={`${teamCount}`} />
            <SummaryRow label="Risks" value={`${riskCount}`} />
            <SummaryRow label="Sections drafted" value={`${sectionsReady} / ${totalSections}`} />
            <SummaryRow label="Logo uploaded" value={logoFile ? '✓' : '—'} />
            <SummaryRow label="Project image" value={projectImageFile ? '✓' : '—'} />
            <SummaryRow label="Funding amount" value={data.funding_amount ? `${data.currency} ${data.funding_amount}` : '—'} />
            <SummaryRow label="Grant utilization rows" value={`${grantCount}`} />
            <SummaryRow label="Template" value={templateMeta?.name || '—'} />
          </CardContent>
        </Card>

        {jobResult && (jobResult.status === 'preview_ready' || jobResult.status === 'completed') ? (
          <Card className="border-primary/30 bg-primary/5" data-testid="result-card">
            <CardContent className="py-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-primary" />
                  <div>
                    <p className="font-semibold text-foreground">Your proposal is ready</p>
                    <p className="text-sm text-muted-foreground">
                      {formatResultCardMeta(jobResult, credits)}
                    </p>
                  </div>
                </div>
                <Button onClick={handleDownload} data-testid="download-btn" className="glow-button">
                  <Download className="mr-2 h-4 w-4" />
                  {jobResult.status === 'preview_ready' ? `Unlock & Download — ${credits} credits` : 'Download PDF'}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="py-5 flex items-center justify-between gap-3">
              <div className="space-y-1">
                <div className="text-sm font-medium">Generate your proposal</div>
                <div className="text-xs text-muted-foreground">
                  {credits} credits will be deducted when you generate. Remaining sections are filled in automatically.
                </div>
              </div>
              <Button
                onClick={handleFinalGenerate}
                disabled={generating || polling}
                className="glow-button px-8"
                data-testid="generate-btn"
              >
                {generating
                  ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{polling ? 'Building…' : 'Submitting…'}</>
                  : <><FileText className="mr-2 h-4 w-4" /> Generate Proposal</>}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const stepRenderers = {
    1: renderStep1, 2: renderStep2, 3: renderStep3, 4: renderStep4,
    5: renderStep5, 6: renderStep6, 7: renderStep7, 8: renderStep8, 9: renderStep9,
  };
  const currentStep = STEPS[step - 1];
  const Icon = currentStep.icon;

  return (
    <div className="min-h-screen bg-background pb-16">
      {/* Header */}
      <header className="border-b border-border bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-3">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2" data-testid="back-to-dashboard">
            <DocManiaLogoImage size={28} />
          </button>
          <div className="flex items-center gap-3">
            <CreditBadge balance={user?.credit_balance || 0} />
          </div>
        </div>
      </header>

      <div className="bp-header">
        <button className="bp-back-btn" onClick={() => navigate('/documents/business-proposal/categories')} type="button">← Back</button>
        <div className="bp-header-title">
          <h1>Business Grant Proposal Builder</h1>
          <p>Complete all steps to generate a fundable grant proposal</p>
        </div>
      </div>

      <main className="mx-auto max-w-5xl px-4 py-6 space-y-5">
        <ProposalStepIndicator steps={STEPS} step={step} />

        {showDraftPill && (
          <div
            className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm"
            data-testid="draft-restored-pill"
          >
            <div className="flex items-center gap-2 text-emerald-900">
              <CheckCircle className="h-4 w-4" />
              <span>
                <strong>Draft restored</strong> — we picked up where you left off.
                We'll keep auto-saving every 30 seconds.
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={dismissDraftPill} data-testid="dismiss-draft-pill">
                Dismiss
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={clearDraft} data-testid="clear-draft-btn" className="text-destructive hover:text-destructive">
                Reset draft
              </Button>
            </div>
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Icon className="h-5 w-5 text-primary" /> {currentStep.title}
            </CardTitle>
          </CardHeader>
          <CardContent>{stepRenderers[step]()}</CardContent>
        </Card>

        {/* Footer nav */}
        <div className="flex items-center justify-between gap-3 pt-2">
          <Button type="button" variant="outline" onClick={goBack} disabled={step === 1} data-testid="wizard-back-btn">
            <ArrowLeft className="mr-1 h-4 w-4" /> Back
          </Button>
          <div className="text-xs text-muted-foreground">{step}/{WIZARD_STEP_COUNT}</div>
          {step < WIZARD_STEP_COUNT ? (
            <Button type="button" onClick={goNext} data-testid="wizard-next-btn" className="glow-button">
              Next <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          ) : (
            <span /> /* Step 7 has its own Generate / Download buttons */
          )}
        </div>
      </main>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-center justify-between rounded-md border bg-card/50 px-3 py-2">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-foreground truncate ml-2">{value}</span>
    </div>
  );
}
